import { StyledCartAmountSelector } from './style';
import { ProductData } from 'types/ProductData';
import {
  CartActionType,
  useCartDispatch,
  getCartStorageKey,
} from 'public/context/CartContext';
import { useRefetchCartProducts } from 'hooks/useRefetchCartProducts';
import { Tooltip } from 'ui';
import { useIntl } from 'react-intl';
import useMarketType from '../../utils/useMarketType';
import { useDebounce } from '../../utils/useDebounce';
import { useEffect, useState } from 'react';
import { InputNumber } from 'ui/InputNumber';

interface CartAmountSelectorProps {
  product: ProductData | undefined;
  unitType?: string;
  amountCart: number;
  className?: string;
}

const CartAmountSelector = ({
  product,
  unitType,
  amountCart = 0,
  className,
}: CartAmountSelectorProps) => {
  const [search, setSearch] = useState(0);
  const intl = useIntl();
  const dispatch = useCartDispatch();
  const { refetchCartProducts } = useRefetchCartProducts();
  const { isEmarket } = useMarketType();
  const productAmount = product?.product_amounts?.find((amount) =>
    isEmarket ? amount.type === 'EMARKET' : amount.type !== 'EMARKET'
  );
  const reservation = productAmount?.wholesale_reserve;

  const stableSearch = useDebounce(search, 1000);

  useEffect(() => {
    if (!stableSearch) {
      return;
    }

    try {
      const rawFarmProducts = localStorage.getItem(
        getCartStorageKey('farmProducts')
      );
      const rawPriceTotal = localStorage.getItem(
        getCartStorageKey('priceTotal')
      );
      const rawCoupons = localStorage.getItem(getCartStorageKey('coupons'));
      const cartId = localStorage.getItem(getCartStorageKey('cartId'))
        ? parseInt(localStorage.getItem(getCartStorageKey('cartId'))!)
        : undefined;

      const farmProducts = rawFarmProducts ? JSON.parse(rawFarmProducts) : [];
      const priceTotal = rawPriceTotal ? JSON.parse(rawPriceTotal) : 0;
      const coupons = rawCoupons ? JSON.parse(rawCoupons) : [];

      if (
        !Array.isArray(farmProducts) ||
        farmProducts.length === 0 ||
        !farmProducts.every(isFarmProductValid)
      ) {
        return;
      }

      refetchCartProducts(farmProducts, priceTotal, coupons, cartId);
    } catch (error) {
      return;
    }
  }, [stableSearch]);

  if (!product) return null;

  const isMaxAmountReached =
    productAmount?.storage_limit_max != null &&
    amountCart >= productAmount.storage_limit_max;

  const isFarmProductValid = (farm: any): boolean => {
    if (
      !farm.farm_id ||
      !farm.products ||
      !Array.isArray(farm.products) ||
      farm.products.length === 0 ||
      !farm.farmPriceTotals ||
      typeof farm.farmPriceTotals.totalWithDiscount !== 'number' ||
      typeof farm.farmPriceTotals.totalWithoutDiscount !== 'number' ||
      typeof farm.farmPriceTotals.totalVat !== 'number'
    ) {
      return false;
    }

    for (const product of farm.products) {
      if (typeof product.amount !== 'number' || product.amount <= 0)
        return false;
    }

    return true;
  };

  const handleIncrement = () => {
    const amount = productAmount?.step ?? 0;

    //all validation happens in validateProduct function
    dispatch({
      type: CartActionType.ADD_ITEM,
      product: product,
      amount,
    });

    setSearch((old) => old + 1);
  };

  const handleDecrement = () => {
    const amount = -1 * (productAmount?.step ?? 0);
    const reservation = productAmount?.wholesale_reserve;

    if (
      amountCart + amount <
        Math.max(
          productAmount?.storage_limit_min ?? 1,
          productAmount?.step ?? 0
        ) ||
      (!productAmount?.storage_stock_count &&
        reservation?.amount_from! > amountCart + amount)
    ) {
      dispatch({
        type: CartActionType.REMOVE_ITEM,
        productId: product.id,
        farmId: product.farm_id,
      });
    } else {
      dispatch({
        type: CartActionType.ADD_ITEM,
        product: product,
        amount,
      });
    }

    setSearch((old) => old + 1);
  };

  const handleInputChange = (value: number | string | undefined | null) => {
    if (value === null || value === undefined || value === '') {
      return;
    }

    let newAmount = typeof value === 'string' ? parseFloat(value) : value;

    if (isNaN(newAmount) || newAmount < 0) {
      return;
    }

    const step = productAmount?.step || 1;
    newAmount = Math.round(newAmount / step) * step;

    const minAmount = Math.max(productAmount?.storage_limit_min ?? 1, step);

    if (
      productAmount?.storage_limit_max &&
      newAmount > productAmount.storage_limit_max
    ) {
      const maxRounded =
        Math.floor(productAmount.storage_limit_max / step) * step;
      if (maxRounded >= minAmount) {
        newAmount = maxRounded;
      } else {
        return;
      }
    }

    if (
      productAmount?.storage_stock_count &&
      newAmount > productAmount.storage_stock_count
    ) {
      const stockRounded =
        Math.floor(productAmount.storage_stock_count / step) * step;
      if (stockRounded >= minAmount) {
        newAmount = stockRounded;
      } else {
        return;
      }
    }

    if (newAmount < minAmount) {
      dispatch({
        type: CartActionType.REMOVE_ITEM,
        productId: product.id,
        farmId: product.farm_id,
      });
      setSearch((old) => old + 1);
      return;
    }

    const difference = newAmount - amountCart;

    if (difference === 0) {
      return;
    }

    dispatch({
      type: CartActionType.ADD_ITEM,
      product: product,
      amount: difference,
    });

    setSearch((old) => old + 1);
  };

  const handleInputBlur = (event?: React.ChangeEvent<HTMLInputElement>) => {
    const step = productAmount?.step || 1;
    const roundedAmount = Math.round(amountCart / step) * step;

    if (roundedAmount !== amountCart) {
      const difference = roundedAmount - amountCart;

      const minAmount = Math.max(productAmount?.storage_limit_min ?? 1, step);

      if (roundedAmount < minAmount) {
        dispatch({
          type: CartActionType.REMOVE_ITEM,
          productId: product.id,
          farmId: product.farm_id,
        });
      } else {
        dispatch({
          type: CartActionType.ADD_ITEM,
          product: product,
          amount: difference,
        });
      }

      setSearch((old) => old + 1);
    }
  };

  const content = (
    <div
      className={className ? 'amount-selector ' + className : 'amount-selector'}
    >
      <button onClick={handleDecrement} className="btn-amount">
        -
      </button>
      <div className={'unit-unit-type'}>
        <InputNumber
          value={amountCart}
          controls={false}
          onChange={handleInputChange}
          onBlur={handleInputBlur}
        />
        <span>{unitType}</span>
      </div>
      <Tooltip
        id={'max-amount-reached-tooltip'}
        title={
          isMaxAmountReached
            ? intl.formatMessage({ id: 'general.max_amount_reached' })
            : null
        }
      >
        <span>
          <button onClick={handleIncrement} className={'btn-amount'}>
            +
          </button>
        </span>
      </Tooltip>
    </div>
  );

  return (
    <StyledCartAmountSelector>
      {isEmarket || amountCart < productAmount?.storage_stock_count! ? (
        content
      ) : (
        <Tooltip
          getPopupContainer={(triggerNode: HTMLElement) => triggerNode}
          title={
            <div>
              <div>{intl.formatMessage({ id: 'cart.tooltip_text' })}</div>
              <div className="tooltip-availability">
                {intl.formatMessage({ id: 'general.availability_amount' })}:{' '}
                {productAmount?.storage_stock_count}
                {productAmount?.storage_unit},
              </div>
              {(reservation?.amount_from || reservation?.amount_to) && (
                <div className="tooltip-reservations">
                  {intl.formatMessage({ id: 'general.for_reservation' })}:{' '}
                  {reservation?.amount_from}-{reservation?.amount_to}
                  {productAmount?.storage_unit}
                </div>
              )}
            </div>
          }
        >
          {content}
        </Tooltip>
      )}
    </StyledCartAmountSelector>
  );
};

export default CartAmountSelector;
